/**
 * What the clients screens show, from the caller's `clients` access grant (020 § Access
 * grants, the owner's decision of 2026-09-21) and their team role.
 *
 * Before that decision this read the role alone, and `member` stood for both reading an
 * account and creating one. The two are now different levels of one area, so they are
 * different fields here: `read` shows the screens at all, `write` shows the controls that
 * change something, and `admin` is `write` narrowed to the roles that reach every account
 * rather than only their own (001 FR-022, unchanged).
 *
 * A caller with no `clients` grant holds none of the three and the mode is absent, so these
 * flags describe a caller who is already inside the area. The screens hide a control the
 * caller cannot use rather than disabling it; the server still decides every write.
 */
import { accessOf, holdsArea } from "../shell/state/access.ts";

export interface AccountAccess {
  /** `workspace.account.read`: the lists, the pages and the account names on an item. */
  readonly read: boolean;
  /** `workspace.account.create` and `workspace.account_group.create`, which are writes. */
  readonly write: boolean;
  /** `workspace.account.edit` and `.archive` over **every** account and group, which is `write` held by an owner or admin. */
  readonly admin: boolean;
  /** `workspace.milestone_track.manage`, the milestone templates' writes (020 § Milestone templates): an owner or admin holding the `clients` area at write, as the key is gated (020 FR-020-178), which is `admin`. */
  readonly templates: boolean;
  /** A Team owner holding the area at write: the one who may move a client past the tasks of its milestone with an override (020 FR-020-271, 2026-09-30). */
  readonly owner: boolean;
  /** The area at `portal`, a portal worker (028 FR-028-001): sets up a client's tickets and opens them from its page (FR-028-049). */
  readonly portal: boolean;
  readonly actorId: string | null;
}

interface MeForAccounts {
  readonly id: string;
  readonly memberships: readonly {
    readonly slug: string;
    readonly role: string;
    readonly access?: readonly { readonly area: string; readonly level: string }[];
  }[];
}

export function accountAccessOf(me: MeForAccounts | undefined, ws: string): AccountAccess {
  const role = me?.memberships.find((membership) => membership.slug === ws)?.role ?? null;
  // `accessOf` already leaves out a guest's `clients` grant, which admits them to no account key.
  const grants = accessOf(me, ws);
  const write = holdsArea(grants, "clients", "write");
  return {
    read: holdsArea(grants, "clients", "read"),
    write,
    admin: write && (role === "owner" || role === "admin"),
    templates: write && (role === "owner" || role === "admin"),
    owner: write && role === "owner",
    portal: holdsArea(grants, "clients", "portal"),
    actorId: me?.id ?? null,
  };
}

/**
 * Whether the caller edits an account or a group: an owner or admin holding the area at write.
 * A client and a group have had no owner of their own since 2026-09-27 (the organisation owns
 * its clients), so no relation to the row widens this.
 */
export function canEdit(access: AccountAccess): boolean {
  return access.admin;
}

/**
 * Whether the caller edits, deletes and restores a call or meeting entry (020 FR-020-244,
 * `workspace.account.interaction.edit`): an owner or admin, or the actor who logged it, each
 * holding the `clients` area at write, which is checked at every write, so a logger whose grant
 * was narrowed to read edits nothing.
 */
export function canEditInteraction(access: AccountAccess, entry: { readonly logged_by: { readonly id: string } }): boolean {
  if (!access.write || access.actorId === null) return false;
  return access.admin || entry.logged_by.id === access.actorId;
}
